import { isDefined } from "@clipboard-health/util-ts";
import { isAfter, subMinutes } from "date-fns";

interface BookedShift {
  start: string;
  workplaceId: string;
}

export interface PreShiftOrientationEvaluation {
  hasPreShiftOrientation: boolean;
  adjustedStart?: Date;
  orientationLengthInMinutes?: number;
}

/**
 * Determines whether an open shift should be displayed as a "pre-shift orientation shift".
 *
 * The worker arrives early only when ALL of the following are true:
 *   1. The workplace has `preShiftOrientationLengthInMinutes` configured.
 *   2. The worker does NOT have an orientation credential for this workplace.
 *   3. The worker does NOT have an active `PreShiftOrientation` record at this
 *      workplace (another booked shift already holds the designation).
 *   4. The worker does NOT have an earlier booked shift at this workplace
 *      (backstop for async job races and pre-launch bookings).
 */
export function hasPreShiftOrientation(params: {
  workplaceId: string;
  shiftStart: string;
  preShiftOrientationLengthInMinutes?: number;
  workplacesWithOrientationCredentials: Set<string>;
  workplacesWithUpcomingPreShiftOrientation: Map<string, Date>;
  bookedShifts: BookedShift[];
}): PreShiftOrientationEvaluation {
  const {
    workplaceId,
    shiftStart,
    preShiftOrientationLengthInMinutes,
    workplacesWithOrientationCredentials,
    workplacesWithUpcomingPreShiftOrientation,
    bookedShifts,
  } = params;

  if (!isDefined(preShiftOrientationLengthInMinutes)) {
    return { hasPreShiftOrientation: false };
  }

  if (workplacesWithOrientationCredentials.has(workplaceId)) {
    return { hasPreShiftOrientation: false };
  }

  const shiftStartDate = new Date(shiftStart);

  // Only suppress if the existing pre-shift orientation/booked shift starts earlier than the current shift.
  // If the current shift starts earlier, the pre-shift orientation will be transferred
  // to it upon booking, so we should still show the orientation card.
  const existingOrientationStart = workplacesWithUpcomingPreShiftOrientation.get(workplaceId);
  if (isDefined(existingOrientationStart) && !isAfter(existingOrientationStart, shiftStartDate)) {
    return { hasPreShiftOrientation: false };
  }
  const hasEarlierBookedShiftAtWorkplace = bookedShifts.some(
    (bookedShift) =>
      bookedShift.workplaceId === workplaceId &&
      !isAfter(new Date(bookedShift.start), shiftStartDate)
  );

  if (hasEarlierBookedShiftAtWorkplace) {
    return { hasPreShiftOrientation: false };
  }

  return {
    hasPreShiftOrientation: true,
    adjustedStart: subMinutes(shiftStartDate, preShiftOrientationLengthInMinutes),
    orientationLengthInMinutes: preShiftOrientationLengthInMinutes,
  };
}
